import React from 'react';
import classNames from 'classnames';
import { User } from 'models/User';
import { useTranslation } from 'react-i18next';
import utils, { getUserFullName, normalizeEmail } from 'utils';
import { formatPhoneNumber } from 'phoneNumberUtils';
import { EnquiryQuestionType } from 'router/subrouters/Dash/subrouters/Business/components/EnquirySlider/utils';
import ShowEnquiryAnswers from 'router/subrouters/Dash/subrouters/private/components/ShowEnquiryAnswers';
import VerifiedWithCopy from '../../ContactUserInfo/components/VerifiedWithCopy';
import { AvatarWithInitials } from 'components/AvatarWithInitials';
import { NEW_WINDOW_PROPS } from 'data/global';
import LinkToIcon from 'icons/LinkTo.icon';

import './BuyerInfo.styles.scss';

type BuyerInfoProps = {
  className?: string;
  buyer: User;
  enquiryQuestions?: EnquiryQuestionType[];
};

const BuyerInfo: React.FC<BuyerInfoProps> = (props) => {
  const { className, buyer, enquiryQuestions } = props;

  const {
    firstName,
    lastName,
    hasVerifiedEmail,
    email,
    hasVerifiedContactNumber,
    phone,
    linkedinAccount,
  } = buyer;

  const fullName = getUserFullName(firstName, lastName);

  const normalizedEmail = normalizeEmail(email);

  const formattedPhone = formatPhoneNumber(phone);

  const { linkedinUrl } = utils.getLinkedinUrlAndVanityName(linkedinAccount);

  const classes = classNames('buyer-info', className);

  const { t } = useTranslation();

  return (
    <div className={classes}>
      <p>{t('nowChattingWith')}</p>

      <div className="buyer-info__info">
        <div className="avatar-and-name">
          <AvatarWithInitials user={buyer} />
          <p>{fullName}</p>
        </div>

        <VerifiedWithCopy
          copyValue={normalizedEmail}
          copyText={t('copyEmail')}
          isVerified={hasVerifiedEmail}
          verifiedText={t('emailVerified')}
          notVerifiedText={t('emailNotVerified')}
        >
          <p>{normalizedEmail}</p>
        </VerifiedWithCopy>

        {formattedPhone && (
          <VerifiedWithCopy
            copyValue={formattedPhone}
            copyText={t('copyPhoneNumber')}
            isVerified={hasVerifiedContactNumber}
            verifiedText={t('phoneNumberVerified')}
            notVerifiedText={t('phoneNumberNotVerified')}
          >
            <p className="phone">{formattedPhone}</p>
          </VerifiedWithCopy>
        )}

        {linkedinAccount && (
          <a href={linkedinUrl} {...NEW_WINDOW_PROPS}>
            {t('viewLinkedinProfile')}
            <LinkToIcon />
          </a>
        )}
      </div>

      <ShowEnquiryAnswers enquiryQuestions={enquiryQuestions} />
    </div>
  );
};

export default BuyerInfo;
